import { svg } from "../store/modules/mail/Constants.js";

/**
 * Convierte un objeto de estilos a string CSS
 * @param {Object} styles - Objeto con estilos CSS
 * @returns {String} String con estilos CSS
 */
const normalizeCssProperty = (property = "") => {
  if (!property || typeof property !== "string") return property;
  if (property.startsWith("--")) return property; // CSS custom properties
  if (property.includes("-")) return property; // already kebab-case
  // camelCase -> kebab-case
  return property.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
};

const stylesToString = (styles) => {
  if (!styles || typeof styles !== "object") return "";
  return Object.entries(styles)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${normalizeCssProperty(key)}: ${value}`)
    .join("; ");
};

/**
 * Escapa caracteres especiales para atributos HTML
 * @param {*} value - Valor a escapar
 * @returns {String} Valor escapado
 */
const escapeAttr = (v) =>
  String(v)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Convierte atributos a string HTML
 * @param {Object} attributes - Objeto con atributos HTML
 * @returns {String} String con atributos HTML
 */
const attributesToString = (attributes) => {
  if (!attributes || typeof attributes !== "object") return "";

  return Object.entries(attributes)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}="${escapeAttr(v)}"`)
    .join(" ");
};

/**
 * Normaliza los datos de tipografía de marca
 * @param {Object} font - Objeto de fuente
 * @returns {{name: string, url: string}|null}
 */
const normalizeBrandFont = (font = {}) => {
  if (!font || typeof font !== "object") return null;
  const name = font.nombre_tipografia || font.name;
  const url = font.url_tipografia || font.url;
  if (!name || !url) return null;
  return { name, url };
};

/**
 * Determina el formato de la fuente a partir de la extensión
 * @param {String} url
 * @returns {String}
 */
const fontFormatFromUrl = (url = "") => {
  const clean = url.split("?")[0] || "";
  const ext = clean.split(".").pop()?.toLowerCase();
  if (ext === "woff2") return "woff2";
  if (ext === "woff") return "woff";
  if (ext === "ttf") return "truetype";
  if (ext === "otf") return "opentype";
  return "woff2";
};

/**
 * Construye la regla @font-face para una fuente de marca
 * @param {{name: string, url: string}} font
 * @returns {String}
 */
const buildFontFace = (font) => {
  if (!font || !font.name || !font.url) return "";
  const format = fontFormatFromUrl(font.url);
  return `@font-face { font-family: '${font.name}'; src: url('${font.url}') format('${format}'); font-weight: 400; font-style: normal; font-display: swap; }`;
};

/**
 * Obtiene colores y fuentes de marca con valores por defecto
 * @param {Object} datosMarca
 * @returns {Object}
 */
export const getBrandTokens = (datosMarca = {}) => {
  const colores = Array.isArray(datosMarca.colores) ? datosMarca.colores : [];
  const primaryColor =
    (colores.find((c) => c?.is_primary) || colores[0])?.color || "#333333";
  const secondaryColor =
    (colores.find((c) => !c?.is_primary) || colores[1] || colores[0])?.color ||
    primaryColor;
  const headerFont = normalizeBrandFont(datosMarca.tipo_header);
  const bodyFont = normalizeBrandFont(datosMarca.tipo_body);

  return {
    primaryColor,
    secondaryColor,
    headerFont,
    bodyFont,
  };
};

/**
 * Genera CSS global basado en la marca
 * @param {Object} brandTokens
 * @returns {String}
 */
const buildBrandCss = (brandTokens) => {
  const { primaryColor, secondaryColor, headerFont, bodyFont } =
    brandTokens || {};
  const fontFaces = [buildFontFace(headerFont), buildFontFace(bodyFont)]
    .filter(Boolean)
    .join("\n");

  // `headerFamily` ya no se calcula acá: la tipografía de los headings la pone
  // `applyBrandDefaultsToModule` inline en el envoltorio del módulo, que sabe
  // por `col.tag` si es heading o párrafo. Declararla también en el CSS global
  // pisaba lo que el usuario eligiera en el panel de estilos.
  const bodyFamily = bodyFont?.name
    ? `'${bodyFont.name}', Arial, sans-serif`
    : "Arial, sans-serif";

  return `
${fontFaces}
:root {
  --brand-primary-color: ${primaryColor || "#333333"};
  --brand-secondary-color: ${secondaryColor || primaryColor || "#4a4a4a"};
}
body {
  font-family: ${bodyFamily};
  color: var(--brand-secondary-color);
  line-height: 1.6;
}
h1, h2, h3, h4, h5, h6 {
  margin: 0 0 0.5rem 0;
}
a {
  color: var(--brand-primary-color);
}
`; // Nota: algunas apps de correo pueden ignorar estilos globales, por eso también se aplican inline
  // Acá NO se declaran `color` ni `font-family` sobre h1-h6 ni sobre
  // `p, li, span, div`: el contenido de un módulo de texto va dentro de un
  // <div> que YA lleva esos valores inline (`applyBrandDefaultsToModule`, que
  // además distingue heading de párrafo por `col.tag`), y el hijo los hereda.
  // Declararlos acá los pisaba —una regla que matchea directamente gana sobre
  // un valor heredado—, así que el color elegido en el panel de estilos se
  // perdía en la vista previa y en el correo enviado.
  // `body` sí los declara: es el ancestro más externo, así que actúa como
  // default heredable y el inline del módulo lo gana sin conflicto.
};

/**
 * Genera CSS de marca scopeado para el builder
 * @param {Object} brandTokens
 * @param {String} scopeSelector
 * @returns {String}
 */
export const buildBrandCssScoped = (
  brandTokens,
  scopeSelector = ".mail-create__builder-scope",
) => {
  const { primaryColor, secondaryColor, headerFont, bodyFont } =
    brandTokens || {};
  const fontFaces = [buildFontFace(headerFont), buildFontFace(bodyFont)]
    .filter(Boolean)
    .join("\n");

  const headerFamily = headerFont?.name
    ? `'${headerFont.name}', ${
        bodyFont?.name ? `'${bodyFont.name}', ` : ""
      }Arial, sans-serif`
    : bodyFont?.name
    ? `'${bodyFont.name}', Arial, sans-serif`
    : "Arial, sans-serif";

  const bodyFamily = bodyFont?.name
    ? `'${bodyFont.name}', Arial, sans-serif`
    : "Arial, sans-serif";

  const scope = scopeSelector || ".mail-create__builder-scope";

  return `
${fontFaces}
${scope} {
  --brand-primary-color: ${primaryColor || "#333333"};
  --brand-secondary-color: ${secondaryColor || primaryColor || "#4a4a4a"};
  --brand-header-font: ${headerFamily};
  --brand-body-font: ${bodyFamily};
}
`;
};

/**
 * Construye variables CSS de marca para usar inline en el builder
 * @param {Object} brandTokens
 * @returns {Object}
 */
export const buildBrandCssVars = (brandTokens) => {
  const { primaryColor, secondaryColor, headerFont, bodyFont } =
    brandTokens || {};

  const headerFamily = headerFont?.name
    ? `'${headerFont.name}', ${
        bodyFont?.name ? `'${bodyFont.name}', ` : ""
      }Arial, sans-serif`
    : bodyFont?.name
    ? `'${bodyFont.name}', Arial, sans-serif`
    : "Arial, sans-serif";

  const bodyFamily = bodyFont?.name
    ? `'${bodyFont.name}', Arial, sans-serif`
    : "Arial, sans-serif";

  return {
    "--brand-primary-color": primaryColor || "#333333",
    "--brand-secondary-color": secondaryColor || primaryColor || "#4a4a4a",
    "--brand-header-font": headerFamily,
    "--brand-body-font": bodyFamily,
  };
};

/**
 * Aplica estilos por defecto de marca a un módulo
 * @param {Object} col
 * @param {Object} brandTokens
 * @returns {Object}
 */
export const applyBrandDefaultsToModule = (col, brandTokens) => {
  const baseStyles = { ...(col?.styles || {}) };
  if (!col || !brandTokens) return baseStyles;

  const primaryColor = brandTokens.primaryColor;
  const secondaryColor = brandTokens.secondaryColor || primaryColor;
  const headerFamily = brandTokens.headerFont?.name
    ? `'${brandTokens.headerFont.name}', ${
        brandTokens.bodyFont?.name ? `'${brandTokens.bodyFont.name}', ` : ""
      }Arial, sans-serif`
    : null;
  const bodyFamily = brandTokens.bodyFont?.name
    ? `'${brandTokens.bodyFont.name}', Arial, sans-serif`
    : null;

  if (col.type === "Text") {
    const tag = (col.tag || "").toLowerCase();
    const isHeading = /^h[1-6]$/.test(tag);
    if (!baseStyles["font-family"]) {
      baseStyles["font-family"] = isHeading
        ? headerFamily || bodyFamily || "Arial, sans-serif"
        : bodyFamily || headerFamily || "Arial, sans-serif";
    }
    if (!baseStyles.color && primaryColor) {
      baseStyles.color = isHeading ? primaryColor : secondaryColor;
    }
  } else if (col.type === "Button") {
    // Distinguir entre Button y Link por el nombre
    const isLink = col.name === "Link";
    
    if (isLink) {
      // Links: solo color primario, sin fondo
      if (!baseStyles.color && primaryColor) {
        baseStyles.color = primaryColor;
      }
      if (!baseStyles["background-color"]) {
        baseStyles["background-color"] = "transparent";
      }
      if (!baseStyles.border) {
        baseStyles.border = "0px solid transparent";
      }
    } else {
      // Botones: fondo con color primario, texto con color secundario o contrastante
      if (!baseStyles["background-color"] && primaryColor) {
        baseStyles["background-color"] = primaryColor;
      }
      if (!baseStyles.color) {
        // Usar secondaryColor si existe, sino blanco para contraste
        baseStyles.color = secondaryColor || "#ffffff";
      }
      if (!baseStyles.border && primaryColor) {
        baseStyles.border = `2px solid ${primaryColor}`;
      }
    }
    
    if (!baseStyles["font-family"] && (headerFamily || bodyFamily)) {
      baseStyles["font-family"] = headerFamily || bodyFamily;
    }
  } else if (col.type === "Social") {
    if (!baseStyles.color && primaryColor) {
      baseStyles.color = primaryColor;
    }
    if (!baseStyles["font-family"] && (bodyFamily || headerFamily)) {
      baseStyles["font-family"] = bodyFamily || headerFamily;
    }
  } else if (col.type === "Divider") {
    if (!baseStyles["border-top"] && primaryColor) {
      baseStyles["border-top"] = `1px solid ${primaryColor}`;
    }
  }

  return baseStyles;
};

/**
 * Renderiza un módulo de Social (redes sociales)
 * @param {Object} col - Configuración del módulo
 * @returns {String} HTML del módulo social
 */
const renderSocialIcon = (socialName, color = "currentColor") => {
  const iconFactory = svg?.[socialName];
  if (typeof iconFactory === "function") {
    return iconFactory(color);
  }
  return "🔗";
};

const renderSocialModule = (col, brandTokens) => {
  if (!Array.isArray(col.content)) return "";

  const enabledSocials = col.content.filter((social) => social.enable);

  const socialLinks = enabledSocials
    .map((social) => {
      const mergedStyles = applyBrandDefaultsToModule(col, brandTokens);
      const resolvedIconColor =
        social?.color ||
        mergedStyles.color ||
        brandTokens?.primaryColor ||
        "currentColor";
      const icon = renderSocialIcon(social.name, resolvedIconColor);
      const url = social.url || "#";
      return `
      <div style="${stylesToString(mergedStyles)}">
        <a href="${escapeAttr(url)}" target="_blank" style="text-decoration: none; color: inherit; display: inline-flex; align-items: center; justify-content: center;" title="${
        social.name
      }">
          ${icon}
        </a>
      </div>
    `;
    })
    .join("");

  return socialLinks;
};

/**
 * Renderiza un módulo según su tipo
 * @param {Object} col - Configuración del módulo
 * @returns {String} HTML del módulo
 */
const renderModule = (col, brandTokens) => {
  const mergedStyles = applyBrandDefaultsToModule(col, brandTokens);
  const styleStr = stylesToString(mergedStyles);
  const attrStr = attributesToString(col.attributes);

  switch (col.type) {
    case "Text":
      // Para texto, el content ya tiene las etiquetas HTML
      return `<div style="${styleStr}" ${attrStr}>${col.content || ""}</div>`;

    case "Image": {
      const imgSrc =
        col.content || "https://via.placeholder.com/600x300?text=Imagen";
      const imgAlt = col.attributes?.alt || "Imagen";
      const { ["img-link"]: imgLink, ...imgAttributes } = col.attributes || {};
      const imgAttrStr = attributesToString(imgAttributes);
      const imgHtml = `<img src="${imgSrc}" alt="${imgAlt}" style="${styleStr}" ${imgAttrStr} />`;
      if (imgLink) {
        const safeLink = escapeAttr(imgLink);
        return `<a href="${safeLink}" target="_blank" rel="noopener noreferrer">${imgHtml}</a>`;
      }
      return imgHtml;
    }

    case "Video": {
      const videoSrc = col.content || "";
      
      if (videoSrc) {
        // Importamos las funciones de youtube.js para manejo de videos
        const getVideoThumbnail = (url) => {
          if (!url) return "";
          const lowerUrl = url.toLowerCase();
          
          // YouTube
          if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
            let videoId = "";
            try {
              if (url.includes("/embed/")) {
                videoId = url.split("/embed/")[1]?.split("?")[0] || "";
              } else if (url.includes("youtu.be/")) {
                videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
              } else {
                const parsed = new URL(url);
                videoId = parsed.searchParams.get("v") || "";
              }
              if (videoId) {
                return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
              }
            } catch (e) {
              console.error(e); 
              
            }
          }
          
          // Vimeo
          if (lowerUrl.includes("vimeo.com")) {
            try {
              const parsed = new URL(url);
              const pathParts = parsed.pathname.split("/").filter(Boolean);
              const vimeoId = pathParts[0] || "";
              if (vimeoId) {
                return `https://vumbnail.com/${vimeoId}.jpg`;
              }
            } catch (e) {
              console.error(e); 
              
            }
          }
          
          // Fallback para videos directos
          return "https://via.placeholder.com/600x338/333333/FFFFFF?text=Video";
        };
        
        const getVideoWatchUrl = (url) => {
          if (!url) return "#";
          const lowerUrl = url.toLowerCase();
          
          // YouTube - convertir a watch URL
          if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) {
            try {
              let videoId = "";
              if (url.includes("/embed/")) {
                videoId = url.split("/embed/")[1]?.split("?")[0] || "";
              } else if (url.includes("youtu.be/")) {
                videoId = url.split("youtu.be/")[1]?.split("?")[0] || "";
              } else {
                const parsed = new URL(url);
                videoId = parsed.searchParams.get("v") || "";
              }
              if (videoId) {
                return `https://www.youtube.com/watch?v=${videoId}`;
              }
            } catch (e) {
              console.error(e); 
              
            }
          }
          
          // Vimeo - convertir a URL limpia
          if (lowerUrl.includes("vimeo.com")) {
            try {
              const parsed = new URL(url);
              const pathParts = parsed.pathname.split("/").filter(Boolean);
              const vimeoId = pathParts[0] || "";
              if (vimeoId) {
                return `https://vimeo.com/${vimeoId}`;
              }
            } catch (e) {
              console.error(e); 
              
            }
          }
          
          // Para cualquier otro link, retornar tal cual
          return url;
        };
        
        const thumbnailUrl = getVideoThumbnail(videoSrc);
        const watchUrl = getVideoWatchUrl(videoSrc);
        
        // Renderizar como link con thumbnail (compatible con Gmail)
        return `
          <a href="${escapeAttr(watchUrl)}" target="_blank" rel="noopener noreferrer" style="display: inline-block; ${styleStr}">
            <img src="${escapeAttr(thumbnailUrl)}" alt="Video" style="max-width: 100%; height: auto; display: block;" ${attrStr} />
          </a>
        `;
      }
      return `<div style="${styleStr}">📹 Video</div>`;
    }

    case "Button": {
      const buttonContent = col.content || {};
      const buttonUrl =
        buttonContent?.link || col.attributes?.href || col.attributes?.url || "#";
      const buttonText =
        buttonContent?.text || col.content || col.name || "Botón";
      return `<a href="${buttonUrl}" style="${styleStr}" ${attrStr}>${buttonText}</a>`;
    }

    case "Social":
      return renderSocialModule({ ...col, styles: mergedStyles }, brandTokens);

    case "Html":
      return `<div style="${styleStr}" ${attrStr}>${col.content || ""}</div>`;

    case "Divider":
      return `<hr style="${styleStr}" ${attrStr} />`;

    case "Spacer":
      return `<div style="${styleStr}" ${attrStr}></div>`;

    default:
      return `<div style="${styleStr}" ${attrStr}>${col.content || ""}</div>`;
  }
};

/**
 * Renderiza el HTML completo del correo desde el JSON del builder
 * @param {Object} builderJson - Configuración completa del correo
 * @returns {String} HTML completo del correo
 */
export const renderHtmlFromBuilderJson = (builderJson) => {

  if (!builderJson || !builderJson.jsonMail || !builderJson.jsonMail.rows) {
    return '<div style="padding: 2rem; text-align: center;">No hay contenido para mostrar</div>';
  }

  const brandTokens = getBrandTokens(builderJson.datosMarca);
  const mailStylesStr = stylesToString({
    ...builderJson.mailStyles,
  });


  const mailContainerStylesStr = stylesToString(
    {  ...builderJson.mailContainerStyles } ||
      {},
  );
  const brandCss = buildBrandCss(brandTokens);

  // Construir el HTML tabla por tabla (mejor para emails)
  const rowsHtml = builderJson.jsonMail.rows
    .map((row) => {
      const cols = Array.isArray(row?.cols) ? row.cols : [];
      const colsHtml = cols
        .map((col) => {
          const containerStyleStr = stylesToString(col.stylesContainer);
          const moduleHtml = renderModule(col, brandTokens);

          return `<td style="${containerStyleStr}">${moduleHtml}</td>`;
        })
        .join("");

      return `
      <tr>
        <td style="padding: 0;">
          <table cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse; border-spacing: 0; margin: 0; width: 100%;">
            <tbody>
              <tr>
                ${colsHtml}
              </tr>
            </tbody>
          </table>
        </td>
      </tr>
    `;
    })
    .join("");

  // HTML completo con estructura de email (preview aislado)
  const PREVIEW_SCOPE_ID = "twup-email-preview";

  // "Scopeamos" el brandCss para que aplique solo dentro del preview root
  // Nota: es un scoper simple. Evita romper @font-face y :root.
  const scopeCss = (css, scopeSelector) => {
    if (!css || typeof css !== "string") return "";
    const lines = css.split("\n");

    let out = [];
    let inAtRuleBlock = false;

    for (let rawLine of lines) {
      const line = rawLine.trim();
      if (!line) {
        out.push(rawLine);
        continue;
      }

      // Mantener @font-face intacto
      if (line.startsWith("@font-face")) {
        inAtRuleBlock = true;
        out.push(rawLine);
        continue;
      }
      if (inAtRuleBlock) {
        out.push(rawLine);
        if (line.includes("}")) inAtRuleBlock = false;
        continue;
      }

      // Evitar tocar :root global (lo reemplazamos por el scope)
      if (line.startsWith(":root")) {
        out.push(rawLine.replace(":root", scopeSelector));
        continue;
      }

      // Para reglas típicas: "body {", "h1, h2 {", "p, li {"
      // Prefijamos selector con scope
      // (No intenta parsear CSS completo, pero sirve para tu caso)
      const idx = rawLine.indexOf("{");
      if (idx > -1) {
        const selectorPart = rawLine.slice(0, idx).trim();
        //const rest = rawLine.slice(idx);

        // No tocar @... (ya cubrimos font-face, pero por si acaso)
        if (selectorPart.startsWith("@")) {
          out.push(rawLine);
          continue;
        }

        // Si es "body" => "#scope"
        // Si es "html, body" => "#scope" (y si querés, podrías agregar html dentro)
        const scopedSelectors = selectorPart
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => {
            if (s === "body" || s === "html" || s === "html body" || s === "html, body") {
              return scopeSelector;
            }
            // Prefix normal: "#scope <selector>"
            return `${scopeSelector} ${s}`;
          })
          .join(", ");

        out.push(rawLine.replace(selectorPart, scopedSelectors));
        continue;
      }

      out.push(rawLine);
    }

    return out.join("\n");
  };

  const brandCssScopedForPreview = scopeCss(brandCss, `#${PREVIEW_SCOPE_ID}`);

  const completeHtml = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Vista previa del correo</title>

  <style>
    /* Baseline (quirúrgico) para evitar sorpresas en preview web */
    html, body { margin: 0; padding: 0; }
    body { background: #f5f6f8; }

    /* Root del preview: acá vive TODO */
    #${PREVIEW_SCOPE_ID} {
      ${mailContainerStylesStr || ""}
      box-sizing: border-box;
    }
    #${PREVIEW_SCOPE_ID}, 
    #${PREVIEW_SCOPE_ID} * {
      box-sizing: border-box;
    }

    /* Defaults email-friendly */
    #${PREVIEW_SCOPE_ID} table { border-collapse: collapse; border-spacing: 0; }
    #${PREVIEW_SCOPE_ID} td { vertical-align: top; }
    #${PREVIEW_SCOPE_ID} img { border: 0; outline: none; text-decoration: none; display: block; height: auto; max-width: 100%; }
    #${PREVIEW_SCOPE_ID} a { text-decoration: none; }

    /* Brand CSS scopeado */
    ${brandCssScopedForPreview}
  </style>
</head>

<body>
  <div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;mso-hide:all;">
    ${builderJson.introduccion}
    &zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;
  </div>
  <div id="${PREVIEW_SCOPE_ID}">
    <div style="${mailStylesStr}">
      <table cellpadding="0" cellspacing="0" role="presentation" style="border-collapse: collapse; border-spacing: 0; width: 100%;">
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  </div>
</body>
</html>
  `;

  return completeHtml;
};

/**
 * Genera una maqueta SVG liviana para previsualizar el builder del mail.
 * Pensado para ser extensible: cada tipo de módulo tiene un renderer simple
 * y si aparecen nuevos tipos, basta con agregarlos al mapa typeRenderers.
 *
 * Formato esperado de builderJson:
 * {
 *   mailStyles: { width: '600px', 'background-color': '#fff', ... },
 *   datosMarca: { colores: [...], tipo_header, tipo_body, ... },
 *   jsonMail: {
 *     rows: [
 *       {
 *         id,
 *         cols: [
 *           { type, name, content, styles, stylesContainer, attributes, tag }
 *         ]
 *       }
 *     ]
 *   }
 * }
 *
 * @param {Object} builderJson - Configuración completa del correo.
 * @returns {String} SVG serializado listo para usar en srcdoc / v-html.
 */
export const renderSVGFromBuilderJson = (
  builderJson,
  classessvg = "rendered-svg",
  toVertical = false,
) => {
  const multiV = toVertical ? 2 : 1;
  const multiVSpaces = toVertical ? 4 : 1;

  const fallback = `<svg xmlns="http://www.w3.org/2000/svg" class="${classessvg}" width="360" height="120"><rect width="100%" height="100%" fill="#f5f5f5"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#777" font-family="Arial, sans-serif" font-size="14">No hay contenido para mostrar</text></svg>`;
  if (
    !builderJson ||
    !builderJson.jsonMail ||
    !Array.isArray(builderJson.jsonMail.rows)
  ) {
    return fallback;
  }

  const pxToNumber = (value, defaultValue) => {
    if (value === 0 || value === "0") return 0;
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value.replace("px", ""));
      if (!Number.isNaN(parsed)) return parsed;
    }
    return defaultValue;
  };

  const brandTokens = getBrandTokens(builderJson.datosMarca);
  const primaryColor = brandTokens.primaryColor || "#333333";
  const secondaryColor = brandTokens.secondaryColor || primaryColor;
  const mailStyles = builderJson.mailStyles || {};
  const canvasWidth = pxToNumber(mailStyles.width, 600);
  const canvasPadding = 16;
  const rowGap = 6 * multiVSpaces;
  const colGap = 6 * multiVSpaces;
  const mailBg = mailStyles["background-color"] || "#ffffff";

  const moduleHeight = (col) => {
    const styleHeight = pxToNumber(col?.styles?.height, null);
    if (styleHeight !== null) return Math.max(styleHeight, 16);

    switch (col?.type) {
      case "Text":
        return 60 * multiV;
      case "Image":
      case "Logo":
        return 150 * multiV;
      case "Video":
        return 150 * multiV;
      case "Button":
      case "Link":
        return 60 * multiV;
      case "Social":
        return 60 * multiV;
      case "Html":
        return 150 * multiV;
      case "Divider":
        return 30 * multiV;
      case "Spacer":
        return pxToNumber(col?.styles?.height, 30) * multiV;
      default:
        return 60 * multiV;
    }
  };

  const rows = builderJson.jsonMail.rows || [];
  if (!rows.length) return fallback;

  // Precalcular altura total
  const innerWidth = Math.max(canvasWidth - canvasPadding * 2, 200);
  const rowMeta = rows.map((row) => {
    const cols = Array.isArray(row?.cols) ? row.cols : [];
    const maxH = cols.length ? Math.max(...cols.map(moduleHeight)) : 48;
    return { cols, height: maxH }; // 10px padding top/bottom dentro de fila
  });

  const totalHeight = rowMeta.reduce(
    (acc, r, idx) => acc + r.height + (idx === rowMeta.length - 1 ? 0 : rowGap),
    canvasPadding * 2,
  );

  const parts = [];
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${canvasWidth}" height="${totalHeight}" viewBox="0 0 ${canvasWidth} ${totalHeight}" role="img" aria-label="Maqueta del correo" class="${classessvg}" >
    <defs>
      <style>
        .mail-bg { fill: ${mailBg}; }
        .row-bg { fill: #f8f9fb; }
        .module-box { fill: #ffffff; stroke: ${primaryColor}; stroke-width: 1.2; }
        .module-title { font-family: Arial, sans-serif; font-size: 12px; font-weight: 700; fill: ${secondaryColor}; }
        .module-text { font-family: Arial, sans-serif; font-size: 11px; fill: ${secondaryColor}; }
        .module-button-text { font-family: Arial, sans-serif; font-size: 11px; fill: #ffffff; font-weight: 600; }
      </style>
    </defs>
    <rect class="mail-bg" x="0" y="0" width="${canvasWidth}" height="${totalHeight}" rx="8" ry="8" />
  `);

  const typeRenderers = {
    Text: ({ x, y, width, height, col }) => {
      const snippet =
        (col?.content || "")
          .toString()
          .replace(/<[^>]+>/g, "")
          .slice(0, 60) || "Texto";
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "Texto"
      }</text>
        <text class="module-text" x="${x + 10}" y="${y + 36}">${snippet}</text>
      `;
    },
    Image: ({ x, y, width, height, col }) => {
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <rect x="${x + 10}" y="${y + 26}" width="${width - 20}" height="${
        height - 36
      }" rx="6" ry="6" fill="#e8eef7" stroke="#c9d5ea" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "Imagen"
      }</text>
      `;
    },
    Logo: ({ x, y, width, height, col }) => {
      return typeRenderers.Image({
        x,
        y,
        width,
        height,
        col: { ...col, name: col?.name || "Logotipo" },
      });
    },
    Video: ({ x, y, width, height, col }) => {
      const cx = x + width / 2;
      const cy = y + height / 2 + 6;
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <rect x="${x + 10}" y="${y + 26}" width="${width - 20}" height="${
        height - 36
      }" rx="6" ry="6" fill="#e9f0f5" stroke="#c9d5ea" />
        <polygon 
          points="${cx - 4},
                  ${cy - 8} ${cx - 4},
                  ${cy + 8} ${cx + 12},
                  ${cy}" 
          fill="${primaryColor}" opacity="0.9" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "Video"
      }</text>
      `;
    },
    Button: ({ x, y, width, height, col }) => {
      const label =
        (col?.content && col.content.text) || col?.content || "Botón";
      const btnWidth = Math.min(width - 40, 140);
      const btnX = x + (width - btnWidth) / 2;
      const btnY = y + height / 2;
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <rect x="${btnX}" y="${
        btnY - 16
      }" width="${btnWidth}" height="32" rx="16" ry="16" fill="${primaryColor}" opacity="0.9" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "Botón"
      }</text>
        <text class="module-button-text" x="${
          btnX + btnWidth / 2
        }" y="${btnY}" text-anchor="middle" dominant-baseline="middle">${label}</text>
      `;
    },
    Link: ({ x, y, width, height, col }) =>
      typeRenderers.Button({
        x,
        y,
        width,
        height,
        col: { ...col, name: col?.name || "Link" },
      }),
    Social: ({ x, y, width, height, col }) => {
      const icons = Array.isArray(col?.content)
        ? col.content.filter((c) => c.enable).slice(0, 6)
        : [];
      const iconSize = 18;
      const gap = 10;
      const startX = x + 10;
      const baseY = y + height / 2 + 4;
      const circles = icons
        .map((icon, idx) => {
          const cx = startX + idx * (iconSize + gap);
          return `<circle cx="${cx}" cy="${baseY}" r="${
            iconSize / 2
          }" fill="#e9f0f5" stroke="#c9d5ea" /><text class="module-text" x="${
            cx - 6
          }" y="${baseY + 4}" font-size="10">${(icon.name || "?").slice(
            0,
            2,
          )}</text>`;
        })
        .join("");
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "Social"
      }</text>
        ${
          circles ||
          `<text class="module-text" x="${x + 10}" y="${
            y + 38
          }">Anade redes</text>`
        }
      `;
    },
    Html: ({ x, y, width, height, col }) => {
      const snippet =
        (col?.content || "")
          .toString()
          .replace(/<[^>]+>/g, "")
          .slice(0, 40) || "HTML";
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || "HTML"
      }</text>
        <text class="module-text" x="${x + 10}" y="${y + 36}">${snippet}</text>
      `;
    },
    Divider: ({ x, y, width, height, col }) => {
      const lineY = y + height / 2 + 6;
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="6" ry="6" />
        <text class="module-title" x="${x + 10}" y="${y + 16}">${
        col?.name || "Divisor"
      }</text>
        <line x1="${x + 10}" y1="${lineY}" x2="${
        x + width - 10
      }" y2="${lineY}" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" />
      `;
    },
    Spacer: ({ x, y, width, height, col }) => {
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="6" ry="6" fill="#f0f2f5" stroke="#d8dde6" />
        <text class="module-title" x="${x + 10}" y="${y + 16}">${
        col?.name || "Espaciador"
      }</text>
      `;
    },
    default: ({ x, y, width, height, col }) => {
      return `
        <rect class="module-box" x="${x}" y="${y}" width="${width}" height="${height}" rx="8" ry="8" />
        <text class="module-title" x="${x + 10}" y="${y + 18}">${
        col?.name || col?.type || "Modulo"
      }</text>
      `;
    },
  };

  let currentY = canvasPadding;
  rowMeta.forEach((row, rowIndex) => {
    const cols = row.cols;
    const colCount = Math.max(cols.length, 1);
    const availableWidth = innerWidth - colGap * (colCount - 1);
    const colWidth = availableWidth / colCount;
    const rowHeight = row.height;
    parts.push(
      `<rect class="row-bg" x="${canvasPadding}" y="${currentY}" width="${innerWidth}" height="${rowHeight}" rx="10" ry="10" opacity="0.5" />`,
    );
    cols.forEach((col, colIndex) => {
      const renderer = typeRenderers[col?.type] || typeRenderers.default;
      const x = canvasPadding + colIndex * (colWidth + colGap);
      const modHeight = moduleHeight(col);
      const modY = currentY + (rowHeight - modHeight) / 2;
      parts.push(
        renderer({
          x,
          y: modY,
          width: colWidth,
          height: modHeight,
          col,
          rowIndex,
          colIndex,
        }),
      );
    });
    currentY += rowHeight + rowGap;
  });

  parts.push("</svg>");
  return parts.join("");
};
